import { initializeApp, FirebaseApp } from "firebase/app";
import axios from "axios";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  GithubAuthProvider,
  sendPasswordResetEmail,
  confirmPasswordReset,
  sendEmailVerification,
  applyActionCode,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  browserSessionPersistence,
  Auth
} from "firebase/auth";

// Firebase config & application state
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

// Firebase providers
const googleProvider = new GoogleAuthProvider();
const githubProvider = new GithubAuthProvider();

// Initialize with empty config that will be updated later
let firebaseConfig = {
  apiKey: "",
  authDomain: "",
  projectId: "",
  messagingSenderId: "",
  appId: ""
};

// Load the configuration from the server and initialize Firebase
async function initializeFirebase() {
  try {
    // First try to load from window object if already set (for compatibility)
    if (typeof window !== 'undefined' && (window as any).__FIREBASE_CONFIG__) {
      console.log('Loading Firebase config from window object');
      firebaseConfig = (window as any).__FIREBASE_CONFIG__;
    } 
    // Then try to load from environment variables
    else if (import.meta.env.VITE_FIREBASE_API_KEY) {
      console.log('Loading Firebase config from environment variables');
      firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
        appId: import.meta.env.VITE_FIREBASE_APP_ID || ""
      };
    }
    
    // Finally, try to load from the API (this will override previous methods if successful)
    try {
      const response = await axios.get('/api/config');
      
      if (response.data?.firebase?.apiKey) {
        firebaseConfig = response.data.firebase;
        
        // Store in window for potential future use
        if (typeof window !== 'undefined') {
          (window as any).__FIREBASE_CONFIG__ = firebaseConfig;
        }
      } else {
        console.warn('Server returned invalid Firebase config');
      }
    } catch (error) {
      console.error('Failed to fetch Firebase config from server:', error);
    }
    
    // Check if we have a valid API key before initializing
    if (!firebaseConfig.apiKey) {
      console.error('No Firebase API key available, authentication will fail');
      return null;
    }
    
    // Initialize Firebase with the loaded config
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    
    // Setup persistence
    if (auth) {
      try {
        await setPersistence(auth, indexedDBLocalPersistence);
      } catch (error) {
        console.warn('Failed to set indexed DB persistence, falling back to session:', error);
        try {
          await setPersistence(auth, browserSessionPersistence);
        } catch (sessionError) {
          console.error('Failed to set any persistence method:', sessionError);
        }
      }
    }
    
    return { app, auth };
  } catch (error) {
    console.error('Firebase initialization failed:', error);
    return null;
  }
}

// Initialize Firebase and export a promise that resolves when ready
export const firebaseReady = initializeFirebase();

// Get auth and app with safety checks
const getFirebaseAuth = async (): Promise<Auth> => {
  await firebaseReady;
  if (!auth) throw new Error('Firebase auth not initialized');
  return auth;
};

const getFirebaseApp = async (): Promise<FirebaseApp> => {
  await firebaseReady;
  if (!app) throw new Error('Firebase app not initialized');
  return app;
};

// Wrapped authentication functions that handle async initialization
export const signUpWithEmail = async (email: string, password: string) => {
  const auth = await getFirebaseAuth();
  return createUserWithEmailAndPassword(auth, email, password);
};

export const signInWithEmail = async (email: string, password: string) => {
  const auth = await getFirebaseAuth();
  return signInWithEmailAndPassword(auth, email, password);
};

// Debug logger function
const logAuthEvent = (event: string, details?: unknown) => {
  const timestamp = new Date().toISOString();
  console.log(`[AUTH ${timestamp}] ${event}`, details || '');
  
  // Log to localStorage for debugging across redirects
  try {
    const logs = JSON.parse(localStorage.getItem('auth_debug_logs') || '[]');
    logs.push({ timestamp, event, details: details || null });
    // Keep only the last 50 logs
    while (logs.length > 50) logs.shift();
    localStorage.setItem('auth_debug_logs', JSON.stringify(logs));
  } catch (e) {
    console.error("Failed to store debug log:", e);
  }
};

export const signInWithGoogle = async (useRedirect = false) => {
  const auth = await getFirebaseAuth();
  
  // Configure Google provider
  googleProvider.setCustomParameters({
    'prompt': 'select_account',
    'login_hint': localStorage.getItem('last_login_email') || ''
  });

  if (useRedirect) {
    logAuthEvent('Attempting Google auth with redirect');
    return signInWithRedirect(auth, googleProvider);
  }
  
  // Use popup for compatibility with older browsers
  logAuthEvent('Attempting Google auth with popup');
  return signInWithPopup(auth, googleProvider);
};

export const signInWithGithub = async (useRedirect = false) => {
  const auth = await getFirebaseAuth();
  logAuthEvent('GitHub auth requested');

  // Configure GitHub provider
  githubProvider.setCustomParameters({
    'prompt': 'select_account',
    'state': generateRandomState(),
    'allow_signup': 'true'
  });

  // Add scopes
  githubProvider.addScope('user');
  githubProvider.addScope('user:email');

  if (useRedirect) {
    logAuthEvent('Attempting GitHub auth with redirect');
    return signInWithRedirect(auth, githubProvider);
  }
  
  try {
    logAuthEvent('Attempting GitHub auth with popup');
    const result = await signInWithPopup(auth, githubProvider);

    if (result?.user) {
      logAuthEvent('GitHub popup auth successful', {
        providerId: result.providerId,
        email: result.user.email,
        displayName: result.user.displayName
      });

      if (result.user.email) {
        localStorage.setItem('last_login_email', result.user.email);
      }
    }

    return result;
  } catch (error) {
    logAuthEvent('GitHub auth error', {
      code: (error as {code?: string}).code,
      message: (error as {message?: string}).message
    });

    if ((error as {code?: string}).code === 'auth/popup-closed-by-user') {
      console.log('User closed the popup, not showing error');
      return null;
    }

    throw error;
  }
};

// Check for redirect result
export const handleRedirectResult = async () => {
  const auth = await getFirebaseAuth();
  
  try {
    logAuthEvent('Checking for redirect result');
    const result = await getRedirectResult(auth);

    if (result) {
      logAuthEvent('Redirect authentication successful', {
        providerId: result.providerId,
        email: result.user.email,
        displayName: result.user.displayName
      });

      if (result.user.email) {
        localStorage.setItem('last_login_email', result.user.email);
      }
    }

    return result;
  } catch (error) {
    logAuthEvent('Redirect authentication error', {
      code: (error as {code?: string}).code,
      message: (error as {message?: string}).message
    });

    throw error;
  }
};

// Helper function to generate a random state parameter for extra security
function generateRandomState() {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

export const resetPassword = async (email: string) => {
  const auth = await getFirebaseAuth();
  return sendPasswordResetEmail(auth, email);
};

export const confirmPasswordResetWithCode = async (code: string, newPassword: string) => {
  const auth = await getFirebaseAuth();
  return confirmPasswordReset(auth, code, newPassword);
};

export const sendVerificationEmail = async (user: import('firebase/auth').User) => {
  return sendEmailVerification(user);
};

export const confirmEmailVerification = async (code: string) => {
  const auth = await getFirebaseAuth();
  return applyActionCode(auth, code);
};

export const changePassword = async (user: import('firebase/auth').User, currentPassword: string, newPassword: string) => {
  // Re-authenticate the user with their current password
  const credential = EmailAuthProvider.credential(user.email!, currentPassword);
  await reauthenticateWithCredential(user, credential);
  
  // Update the password
  return updatePassword(user, newPassword);
};

export const signOut = async () => {
  const auth = await getFirebaseAuth();
  return firebaseSignOut(auth);
};

// Helper function to retrieve debug logs
export const getAuthDebugLogs = () => {
  try {
    return JSON.parse(localStorage.getItem('auth_debug_logs') || '[]');
  } catch (e) {
    console.error("Failed to retrieve debug logs:", e);
    return [];
  }
};

// Export functions that get the initialized auth and app
export { getFirebaseAuth as getAuth, getFirebaseApp as getApp };
